/* iter55f73 — Admin hub page.
 *
 * `/admin` used to be a monolithic 28-tab console. It's now split
 * across three focused pages:
 *
 *   • /admin/finance    — pricing, budgets, plans, accounting, ROI
 *   • /admin/users      — accounts, teams, editorial bookings
 *   • /admin/operations — models, keys, capacity, marketing, QA
 *
 * This hub is what admins land on when they visit `/admin` bare. It
 * shows three big cards linking to each surface, plus quick tiles for
 * the always-on tabs (My Profile, Admin Guide) so a returning admin
 * doesn't have to click through a group page to reach them.
 */
import React, { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { adminApi, adminAuth, adminRetentionApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ShieldCheck, DollarSign, Users, Cog, LogOut, ArrowRight,
  UserCog, LifeBuoy, BookOpen, Store, Languages, TrendingDown, Inbox, Mail,
  Server, Film, Pencil, Tablet, ShieldAlert, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { GROUP_META } from './admin/adminTabGroups';
import EmailProviderHealthBanner from './admin/EmailProviderHealthBanner';
import CompanionDiagBanner from './admin/CompanionDiagBanner';
import EmailInboundSecretsPill from './admin/EmailInboundSecretsPill';
import IsbnBankThresholdBanner from './admin/IsbnBankThresholdBanner';
import ComplianceHealthPill from './admin/ComplianceHealthPill';
import CompanionPingPill from '@/components/admin/CompanionPingPill';   // iter-companion-ping
import PalettePreviewDialog from '@/components/admin/PalettePreviewDialog';   // iter-palette-preview
import { AuthorshipSettingsPanel } from '@/components/admin/AuthorshipSettingsPanel'; // authorship audit
import { Palette } from 'lucide-react';
import ReaderSyncPanel from './admin/ReaderSyncPanel';
import AdminNotificationsBell from '@/components/admin/AdminNotificationsBell';   // iter-reveal2
import PaymentsKillSwitchTile from '@/components/admin/PaymentsKillSwitchTile';   // iter-stripe-kill-tile
import AdminMobileGate from '@/components/admin/AdminMobileGate';
import AdminLoomManuscriptPicker from '@/components/admin/AdminLoomManuscriptPicker';
import AdminAiNovelsPicker from '@/components/admin/AdminAiNovelsPicker';   // 2026-08 — editor Loom bridge
import { applyAdminTheme, readAdminTheme } from './admin/AdminThemeSwitcher';   // Sep-2026 · apply theme on the unauthed login too

const CARDS = [
  {
    key: 'admin',
    label: 'Admin',
    icon: ShieldCheck,
    blurb: GROUP_META.admin.tone,
    tabs: 'Overview · Closed Issues · Permissions · Admin Users · Security',
    tint: '#7A5A9E',
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: DollarSign,
    blurb: GROUP_META.finance.tone,
    tabs: 'Ledger · Balance Sheet · Income · Cash Flow · Equity · Pricing · Aging · Bank Recon',
    tint: '#6A2E2E',
  },
  {
    key: 'users',
    label: 'Users',
    icon: Users,
    blurb: GROUP_META.users.tone,
    tabs: 'Users · Archived · Teams · Roles · Members · Human Services · Editing · Admin Users · Demo Funnel',
    tint: '#2D4A3E',
  },
  {
    key: 'operations',
    label: 'Operations',
    icon: Cog,
    blurb: GROUP_META.operations.tone,
    tabs: 'Models · API Keys · Database · SEO · Landing · Templates · Marketing · ISBN · Help · Activity · Capacity · QA',
    tint: '#3A2418',
  },
  {
    key: 'it',
    label: 'IT',
    icon: Server,
    blurb: GROUP_META.it.tone,
    tabs: 'IT Management · API Keys · Secrets Vault · Cron reports · Log reports · Network health',
    tint: '#4A6B7A',
  },
  {
    key: 'marketing',
    label: 'Marketing & Media',
    icon: Film,
    blurb: GROUP_META.marketing.tone,
    tabs: 'Training media · Production queue · In-panel viewer',
    tint: '#A66A32',
  },
  {
    key: 'editing',
    label: 'Editing & Books',
    icon: Pencil,
    blurb: GROUP_META.editing.tone,
    tabs: 'Editorial Review · Workbench · Bulk Eval · Plot Builder · ISBN · WIPO · Author Agreements · Audiobook Voices',
    tint: '#3E5C3A',
  },
  {
    key: 'indie_reader',
    label: 'Indie Reader Admin',
    icon: Tablet,
    blurb: GROUP_META.indie_reader.tone,
    tabs: 'Diagnostics · Books · Chapters · Covers · Companion Inbox · Reader Sync',
    tint: '#2A4A6A',
  },
];

export default function AdminHubPage() {
  const [authed, setAuthed] = useState(false);
  const [searchParams] = useSearchParams();
  // iter-click-fix — Oct-2026. Coming back from a password-reset
  // confirmation (?user=David) should pre-fill THAT username, not
  // silently fall back to "Administrator" — that mismatch is exactly
  // what made a successful password reset look like a failed login.
  const [user, setUser] = useState(() => searchParams.get('user')?.trim() || 'Administrator');
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [scopes, setScopes] = useState(null);
  // 2026-10 — Mobile Staff biometric lock. This hub IS the real front
  // door (the mobile "Staff" link on the landing page points here) —
  // desktop is never gated; on a phone/tablet, if any admin opted in,
  // the real sign-in form above is hidden behind <AdminMobileGate/>
  // until it passes (or an earlier pass's 5-min cookie is still valid).
  const [gateChecking, setGateChecking] = useState(true);
  const [gateNeeded, setGateNeeded] = useState(false);
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
    if (!isMobile) { setGateChecking(false); return; }
    (async () => {
      try {
        const status = await adminApi.mobileLockStatus();
        if (!status.enabled) return;
        const chk = await adminApi.mobileLockGateCheck();
        if (!chk.passed) setGateNeeded(true);
      } catch {
        // Fail-open — never lock an admin out of their own console.
      } finally { setGateChecking(false); }
    })();
  }, []);

  // Sep-2026 · apply the admin theme on FIRST paint (including the
  // unauthed login screen) so the Fluent/Windows canvas isn't gated
  // behind sign-in.
  useEffect(() => { applyAdminTheme(readAdminTheme()); }, []);

  // Verify existing session on mount.
  useEffect(() => {
    (async () => {
      if (typeof window !== 'undefined' && !adminAuth.user) return;
      try {
        await adminApi.verify();
        setUser(adminAuth.user || 'Administrator');
        setAuthed(true);
      } catch { /* stays on the login form */ }
    })();
  }, []);

  useEffect(() => {
    if (!authed) return;
    adminApi.me().then((r) => setScopes(r?.scopes || [])).catch(() => setScopes([]));
  }, [authed]);

  // Bookstore-only admins are already routed away by AdminPage; we
  // preserve the same behaviour on the hub for consistency.
  useEffect(() => {
    if (!authed || scopes === null) return;
    const hasHs = scopes.includes('coach') || scopes.includes('editor');
    if (!scopes.includes('pro_writer') && !hasHs && scopes.includes('bookstore')) {
      toast.info('This account is bookstore-only — redirecting to the Bookstore admin.');
      setTimeout(() => { window.location.href = '/bookstore/admin'; }, 800);
    }
  }, [authed, scopes]);

  const login = async () => {
    if (!user.trim() || !pwd || busy) return;
    setBusy(true);
    try {
      // NB: adminApi.login is (password, user) — args in that order.
      await adminApi.login(pwd, user.trim());
      setAuthed(true);
      setPwd('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sign-in failed.');
    } finally { setBusy(false); }
  };

  // iter55g47 — Emailed reset-link flow. Backend routes the link to
  // `admin_recovery_email` from app_config; response is 200 whether
  // or not it's configured (enumeration-safe).
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const sendResetLink = async () => {
    if (forgotBusy) return;
    setForgotBusy(true);
    try {
      const adminUrl = `${window.location.origin}/admin/reset-password`;
      await adminApi.requestPasswordReset(adminUrl);
      setForgotSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reset link.');
    } finally { setForgotBusy(false); }
  };

  const logout = async () => {
    try { await adminApi.logout(); } catch { /* ignore */ }
    setAuthed(false);
    adminAuth.user = null;
    if (typeof window !== 'undefined') localStorage.removeItem('admin_user');
  };

  if (!authed) {
    if (gateChecking) {
      return <div className="h-full w-full bg-background" data-testid="admin-hub-gate-checking" />;
    }
    if (gateNeeded) {
      return <AdminMobileGate onPassed={() => setGateNeeded(false)} />;
    }
    return (
      <div className="flex h-full w-full items-center justify-center bg-background" data-testid="admin-hub-login">
        <div className="w-full max-w-sm rounded-md border border-border bg-panel-header/40 p-6 shadow-lg">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary text-primary-foreground"><ShieldCheck className="h-4 w-4" /></div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">The Loom</div>
              <div className="text-[14px] font-semibold leading-tight">Admin Sign-In</div>
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <Label className="text-[11.5px]">Username</Label>
              <Input value={user} onChange={(e) => setUser(e.target.value)} data-testid="admin-hub-user" />
            </div>
            <div>
              <Label className="text-[11.5px]">Password</Label>
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') login(); }} data-testid="admin-hub-pass" />
            </div>
            <Button onClick={login} disabled={busy} className="w-full" data-testid="admin-hub-signin">
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
            {/* iter55g47 — Emailed reset link. */}
            {forgotSent ? (
              <p className="mt-1 rounded-sm border border-emerald-400/50 bg-emerald-50/60 px-2 py-1.5 text-center text-[11px] text-emerald-900" data-testid="admin-hub-reset-sent">
                If a recovery email is configured, a reset link is on its way. It expires in 60 minutes.
              </p>
            ) : (
              <button
                type="button"
                onClick={sendResetLink}
                disabled={forgotBusy}
                data-testid="admin-hub-forgot"
                className="mt-1 w-full text-center text-[11px] text-muted-foreground hover:text-primary hover:underline disabled:opacity-50"
              >
                {forgotBusy ? 'Sending…' : 'Forgot password? Email me a reset link'}
              </button>
            )}
          </div>
          {/* Feb-2026 · Mirrors the same gateway link the public
              sign-in page offers, in reverse — a way out of the admin
              login card back to the marketing landing page. */}
          <Link
            to="/"
            data-testid="admin-hub-back-to-landing"
            className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary hover:underline"
          >
            <BookOpen className="h-3 w-3" /> Back to landing page
          </Link>
          {/* Editor onboarding hint */}
          <div
            data-testid="admin-hub-editor-hint"
            className="mt-3 flex items-center gap-2 rounded-md border border-border/60 bg-secondary/30 px-3 py-2 text-[11px] text-muted-foreground"
          >
            <Pencil className="h-3 w-3 shrink-0 text-primary/60" />
            <span>Editor? Sign in with your editor credentials — you'll be taken directly to the <strong>Editing &amp; Books</strong> panel.</span>
          </div>
        </div>
      </div>
    );
  }

  // Scope shortcut: editors go directly to the Editing & Books panel;
  // coaches (HS only) still land on Users where Human Services lives.
  if (scopes && !scopes.includes('pro_writer')) {
    if (scopes.includes('editor'))
      return <Navigate to="/admin/editing" replace />;
    if (scopes.includes('coach'))
      return <Navigate to="/admin/users" replace />;
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-background text-foreground" data-testid="admin-hub-workspace">
      <header className="flex items-center justify-between border-b border-border bg-panel-header/40 px-4 py-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary text-primary-foreground"><ShieldCheck className="h-3.5 w-3.5" /></div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">The Loom V2.38_f55</div>
            <div className="text-[13px] font-semibold leading-tight">Admin Console · Hub</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Link to="/admin/users?tab=users" data-testid="admin-hub-users-direct-link"><Button variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]"><Users className="h-3.5 w-3.5" /> Users</Button></Link>
          {/* iter-compliance-cal-p2 — Health pill (overdue / due-soon rollup) */}
          <ComplianceHealthPill />
          {/* iter-companion-ping — Companion Reader base-URL reachability */}
          <CompanionPingPill />
          {/* iter-palette-preview — Try candidate main-canvas colors side-by-side */}
          <PalettePreviewTrigger />
          {/* iter-reveal2 — Notifications bell (auto-reveal toasts, etc.) */}
          <AdminNotificationsBell adminToken={adminAuth.token} />
          <Link to="/bookstore/admin"><Button variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]"><Store className="h-3.5 w-3.5" /> Bookstore Admin</Button></Link>
          {/* 2026-10 — Admin Loom bridge. Mints the sandbox writer
           * session first (so /app's own auth probe already finds it),
           * then navigates in. Falls through to a plain nav on failure
           * so this never leaves the admin stuck on a dead click. */}
          <Button
            variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]"
            data-testid="admin-hub-open-loom"
            onClick={async () => {
              try { await adminApi.loomBridge(); } catch { /* fall through */ }
              window.location.href = '/app';
            }}
          >
            <BookOpen className="h-3.5 w-3.5" /> Open Loom (Troubleshooting)
          </Button>
          {/* 2026-08 — Pick a real manuscript and open it inside The
           * Loom for the admin/editor to assess and rewrite directly. */}
          <AdminLoomManuscriptPicker />
          <AdminAiNovelsPicker />
          <Button variant="ghost" size="sm" onClick={logout} className="h-7 gap-1.5 text-[12px] text-destructive" data-testid="admin-hub-logout"><LogOut className="h-3.5 w-3.5" /> Sign out</Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10">
        <div className="mb-4 space-y-2">
          <EmailProviderHealthBanner />
          <CompanionDiagBanner />
          <IsbnBankThresholdBanner />
          <div className="flex flex-wrap items-center gap-2">
            <EmailInboundSecretsPill />
          </div>
        </div>
        <h1 className="text-2xl font-semibold">Where do you want to work?</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          The admin console is organised into three focused surfaces. Pick one to dive in — you can hop between them from the header on any admin page.
        </p>

        {/* iter-stripe-kill-tile — Prominent Stripe payments ON/OFF tile.
            Sits above the three group cards so a compromised key or a
            runaway refund loop can be stopped in two clicks. */}
        <PaymentsKillSwitchTile />

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-hub-cards">
          {CARDS.filter((card) => scopes?.includes('pro_writer') || (
            card.key === 'marketing' && scopes?.includes('marketing_media')
          ) || (
            card.key === 'finance' && scopes?.some((scope) => ['financials', 'viewer', 'ledger_entry', 'ap_ar', 'ap_approver'].includes(scope))
          ) || (
            card.key === 'it' && scopes?.includes('it_management')
          ) || (
            card.key === 'editing' && scopes?.some((scope) => ['editor', 'coach'].includes(scope))
          )).map((c) => (
            <Link
              key={c.key}
              to={GROUP_META[c.key].path}
              data-testid={`admin-hub-card-${c.key}`}
              className="group flex flex-col rounded-md border border-border bg-panel-header/20 p-5 transition hover:border-primary hover:bg-panel-header/40"
            >
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-sm" style={{ backgroundColor: `${c.tint}22`, color: c.tint }}>
                  <c.icon className="h-4 w-4" />
                </div>
                <div className="text-[16px] font-semibold">{c.label}</div>
                <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-primary" />
              </div>
              <p className="mt-3 text-[12.5px] text-muted-foreground">{c.blurb}</p>
              <div className="mt-4 border-t border-border pt-3 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                Includes
              </div>
              <div className="mt-1 text-[11.5px] leading-relaxed text-foreground/80">{c.tabs}</div>
            </Link>
          ))}
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Link
            to="/admin/finance?tab=ledger"
            data-testid="admin-hub-quick-ledger"
            className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary"
          >
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            General Ledger · entries, COA, trial balance, and corrections
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          </Link>
          <Link
            to="/admin/operations"
            data-testid="admin-hub-quick-profile"
            className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary"
          >
            <UserCog className="h-4 w-4 text-muted-foreground" />
            My Profile · always available under every admin surface
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          </Link>
          <Link
            to="/admin/operations"
            data-testid="admin-hub-quick-guide"
            className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary"
          >
            <LifeBuoy className="h-4 w-4 text-muted-foreground" />
            Admin Guide · always available under every admin surface
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          </Link>
          {/* Feb-2026 — Panel Permissions quick-tile (prominent, top of shortcuts) */}
          <Link
            to="/admin/admin?tab=permissions"
            data-testid="admin-hub-quick-permissions"
            className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary"
            style={{ borderLeft: '3px solid #6366f1' }}
          >
            <ShieldAlert className="h-4 w-4" style={{ color: '#6366f1' }} />
            <span><span className="font-medium">Panel Permissions</span> · manage which admin scopes can access each panel tab</span>
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          </Link>
          {/* iter-p2-z — Admin Email Translator quick tile. */}
          <Link
            to="/admin/email-translator"
            data-testid="admin-hub-quick-translator"
            className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary"
          >
            <Languages className="h-4 w-4 text-muted-foreground" />
            Email Translator · translate support replies into any supported language
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          </Link>
          {/* Feb-2026 — Email Send Log viewer */}
          <Link
            to="/admin/emails"
            data-testid="admin-hub-quick-email-log"
            className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary"
          >
            <Mail className="h-4 w-4 text-muted-foreground" />
            Email Log · view + preview every outbound email sent by the platform
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          </Link>
          {/* iter-p3-b — Admin folder-based email inbox. */}
          <Link
            to="/admin/inbox"
            data-testid="admin-hub-quick-inbox"
            className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary"
          >
            <Inbox className="h-4 w-4 text-muted-foreground" />
            Support Inbox · info · complaints · requests · other
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          </Link>
        {/* Invite Editor quick-tile */}
          {scopes?.includes('pro_writer') && <InviteEditorTile />}
          {/* iter-p3-h — Reader Languages Insight. */}
          <Link
            to="/admin/reader-languages"
            data-testid="admin-hub-quick-reader-languages"
            className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary"
          >
            <Languages className="h-4 w-4 text-muted-foreground" />
            Reader Languages · translation-gap alerts by demand vs supply
            <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
          </Link>
        </div>

        {/* iter-p2-y — Downgrade-retention cron stats tile. */}
        <RetentionStatsTile />

        {/* Authorship Standards — copyright compliance thresholds */}
        <AuthorshipSettingsPanel />
      </main>
    </div>
  );
}

function RetentionStatsTile() {
  const [stats, setStats] = useState(null);
  const [err, setErr]     = useState(null);
  useEffect(() => {
    let alive = true;
    adminRetentionApi.stats(90)
      .then((r) => { if (alive) setStats(r); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || 'Could not load retention stats.'); });
  }, []);
  if (err) {
    return (
      <section className="mt-8" data-testid="admin-hub-retention-error">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-[12px] text-destructive">
          {err}
        </div>
      </section>
    );
  }
  if (!stats) {
    return (
      <section className="mt-8" data-testid="admin-hub-retention-loading">
        <div className="rounded-md border border-border bg-panel-header/20 p-4 text-[12px] text-muted-foreground">
          Loading downgrade-retention stats…
        </div>
      </section>
    );
  }
  const s = stats;
  const cohortTotal = (s.cohort_email || 0) + (s.cohort_control || 0);
  const emailPct    = cohortTotal ? Math.round((s.cohort_email || 0) / cohortTotal * 100) : 0;
  const controlPct  = cohortTotal ? 100 - emailPct : 0;
  return (
    <section className="mt-8" data-testid="admin-hub-retention-tile">
      <div className="rounded-md border border-border bg-panel-header/20 p-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary/10 text-primary">
            <TrendingDown className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Last {s.since_days || 90}-day retention cron</div>
            <div className="text-[14px] font-semibold leading-tight">Downgrade-Retention Emails</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-hub-retention-metrics">
          <StatCell k="Eligible"        v={s.eligible ?? 0}          testid="retention-eligible" />
          <StatCell k="Notified"        v={s.notified ?? 0}          testid="retention-notified" />
          <StatCell k="Coupons issued"  v={s.coupons_issued ?? 0}    testid="retention-issued" />
          <StatCell k="Redemption %"    v={`${s.redemption_pct ?? 0}%`} testid="retention-redemption-pct" />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCell k="Active coupons"  v={s.coupons_active ?? 0}    testid="retention-active"  small />
          <StatCell k="Expired coupons" v={s.coupons_expired ?? 0}   testid="retention-expired" small />
          <StatCell k="Cron passes"     v={s.passes_run ?? 0}         testid="retention-passes"  small />
          <StatCell k="Coupon TTL"      v={`${s.coupon_ttl_days ?? 60}d`} testid="retention-ttl"     small />
        </div>
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>A/B cohort split</span>
            <span className="font-mono-stat" data-testid="retention-cohort-split">
              email {s.cohort_email || 0} · control {s.cohort_control || 0}
            </span>
          </div>
          <div className="flex h-2 overflow-hidden rounded-sm bg-panel-header">
            <div className="bg-primary" style={{ width: `${emailPct}%` }} data-testid="retention-cohort-email-bar" />
            <div className="bg-muted-foreground/50" style={{ width: `${controlPct}%` }} data-testid="retention-cohort-control-bar" />
          </div>
          <div className="mt-1 flex justify-between text-[10.5px] text-muted-foreground">
            <span>Email {emailPct}%</span>
            <span>Control {controlPct}%</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCell({ k, v, testid, small }) {
  return (
    <div className="rounded-sm border border-border bg-background/60 px-2 py-1.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className={`font-mono-stat font-semibold ${small ? 'text-[12px]' : 'text-[14px]'}`}>{v}</div>
    </div>
  );
}

// ─── Invite Editor ────────────────────────────────────────────────────────

function InviteEditorTile() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-hub-invite-editor-tile"
        className="flex items-center gap-2 rounded-md border border-border bg-panel-header/20 p-3 text-[13px] hover:border-primary hover:bg-panel-header/40 transition-colors text-left"
        style={{ borderLeft: '3px solid #3E5C3A' }}
      >
        <UserPlus className="h-4 w-4 shrink-0" style={{ color: '#3E5C3A' }} />
        <span><span className="font-medium">Invite an Editor</span> · create an editor-scoped account and email them an activation link</span>
        <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground shrink-0" />
      </button>
      <InviteEditorDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function InviteEditorDialog({ open, onOpenChange }) {
  const [username, setUsername] = useState('');
  const [email,    setEmail]    = useState('');
  const [display,  setDisplay]  = useState('');
  const [busy,     setBusy]     = useState(false);
  const [sent,     setSent]     = useState(false);

  const reset = () => { setUsername(''); setEmail(''); setDisplay(''); setSent(false); };

  const send = async () => {
    if (!username.trim() || !email.trim()) return;
    setBusy(true);
    try {
      await adminApi.inviteEditor({
        username: username.trim(),
        email: email.trim(),
        display_name: display.trim() || undefined,
      });
      setSent(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send invite.';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const handleOpenChange = (v) => { if (!v) reset(); onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm" data-testid="invite-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-primary" />
            Invite an Editor
          </DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="rounded-md border border-emerald-400/50 bg-emerald-50/60 px-4 py-5 text-center dark:bg-emerald-900/20"
               data-testid="invite-editor-success">
            <div className="text-[22px]">&#x2709;&#xfe0f;</div>
            <p className="mt-2 text-[13px] font-medium">Invite sent!</p>
            <p className="mt-1 text-[12px] text-muted-foreground">
              An activation link has been emailed to <strong>{email}</strong>.
              The link expires in 60 minutes.
            </p>
            <Button variant="outline" size="sm" className="mt-4"
                    onClick={() => { reset(); onOpenChange(false); }}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-3 py-1">
              <div>
                <Label className="text-[11.5px]">Username <span className="text-destructive">*</span></Label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. sarah.editor"
                  data-testid="invite-editor-username"
                  className="mt-1"
                />
                <p className="mt-0.5 text-[10.5px] text-muted-foreground">Used to log into the admin console.</p>
              </div>
              <div>
                <Label className="text-[11.5px]">Email address <span className="text-destructive">*</span></Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="editor@example.com"
                  data-testid="invite-editor-email"
                  className="mt-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                />
              </div>
              <div>
                <Label className="text-[11.5px]">Display name <span className="text-muted-foreground text-[10px]">(optional)</span></Label>
                <Input
                  value={display}
                  onChange={(e) => setDisplay(e.target.value)}
                  placeholder="Sarah"
                  data-testid="invite-editor-display"
                  className="mt-1"
                />
                <p className="mt-0.5 text-[10.5px] text-muted-foreground">Shown in the invite email greeting.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={busy || !username.trim() || !email.trim()}
                onClick={send}
                data-testid="invite-editor-submit"
              >
                {busy ? 'Sending…' : 'Send invite'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// iter-palette-preview — Small trigger + host for the swatch dialog.
function PalettePreviewTrigger() {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="palette-preview-trigger"
        title="Preview candidate canvas colors side-by-side"
        className="inline-flex items-center gap-1 rounded-full border border-border bg-panel-header/50 px-2 py-1 text-[10.5px] font-medium text-muted-foreground transition hover:bg-panel-header hover:text-foreground"
      >
        <Palette className="h-3 w-3" /> Palette
      </button>
      <PalettePreviewDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
